import React from 'react';
import PropTypes from 'prop-types';
import { Form, Icon, Popconfirm } from 'antd';
import { autobind } from 'core-decorators';
import _ from 'lodash';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import DataTables from 'components-desktop/DataTables';
import FormInput from 'components-desktop/FormInput';
import FormSelect from 'components-desktop/FormSelect';
import helpers from 'misc/helpers';
import aliyunOssUploader from 'misc/aliyun-oss-uploader';
import caregiverEfileTypes from 'misc/caregiver-efile-types';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class CaregiverEfiles extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      adding: false,
      efiles: [],
      selectedFiles: {},
      uploadingFile: null,
    };
  }

  componentDidMount() {
    this.id = this.parseQueryParam('id', this.props.location);
    this.getCaregiverEfiles();
  }

  async getCaregiverEfiles() {
    const params = { caregiverId: this.id };
    const promise1 = this.callServiceForRemoteObjects('/efile/get_list', params);
    const results = await Promise.all([promise1]);

    this.setState({
      efiles: results[0],
    });
  }

  onAddClick() {
    this.setState({
      adding: true,
    });
  }

  onUpdateClick(record) {
    const validatingFields = [
      `type-${record.id}`,
      `description-${record.id}`,
    ];
    this.props.form.validateFieldsAndScroll(validatingFields, async (err, values) => {
      if (!err) {
        const params = {
          id: record.id,
          type: helpers.extraFormSelectValue(values, `type-${record.id}`),
          description: helpers.extraFormInputValue(values, `description-${record.id}`),
        };
        await this.callService('/efile/update', params);

        this.notifySuccess('Success', 'The efile record has been updated.');
      }
    });
  }

  async onDeleteClick(record) {
    await this.callService('/efile/delete', { id: record.id });

    this.notifySuccess('Success', 'The efile record has been deleted.');

    this.getCaregiverEfiles();
  }

  onSaveClick() {
    const validatingFields = [
      'type-0',
      'description-0',
    ];
    this.props.form.validateFieldsAndScroll(validatingFields, async (err, values) => {
      if (!err) {
        const params = {
          caregiverId: this.id,
          type: helpers.extraFormSelectValue(values, 'type-0'),
          description: helpers.extraFormInputValue(values, 'description-0'),
        };
        await this.callService('/efile/add', params);

        this.notifySuccess('Success', 'A new efile record has been added.');

        this.onCancelClick();
        this.getCaregiverEfiles();
      }
    });
  }

  onCancelClick() {
    this.setState({
      adding: false,
    });
  }

  createColumns() {
    const { form } = this.props;

    return [{
      title: 'Id',
      dataIndex: 'id',
      sorter: (a, b) => {
        if (a.id === 0) {
          return 1;
        } else if (b.id === 0) {
          return -1;
        } else {
          return a.id - b.id;
        }
      },
      defaultSortOrder: 'descend',
    }, {
      title: 'Time',
      dataIndex: 'timeUsString',
      sorter: (a, b) => a.time - b.time,
    }, {
      title: 'Type',
      dataIndex: 'type',
      sorter: (a, b) => a.type?.localeCompare(b.type),
      render: (text, record) => (
        <FormSelect
          width="match_parent"
          height="wrap_content"
          margin="0px 0px"
          form={form}
          name={`type-${record.id}`}
          placeholder="Select..."
          allowClear={false}
          optionGroups={caregiverEfileTypes.ABI}
          initialValue={record.type}
          isRequired
        />
      ),
    }, {
      title: 'File 1',
      dataIndex: 'file1Id',
      render: (text, record) => this.renderFileColumn(text, record, 1),
    }, {
      title: 'File 2',
      dataIndex: 'file2Id',
      render: (text, record) => this.renderFileColumn(text, record, 2),
    }, {
      title: 'File 3',
      dataIndex: 'file3Id',
      render: (text, record) => this.renderFileColumn(text, record, 3),
    }, {
      title: 'Brief Note',
      dataIndex: 'description',
      sorter: (a, b) => a.description?.localeCompare(b.description),
      render: (text, record) => (
        <FormInput
          width="match_parent"
          height="wrap_content"
          margin="0"
          form={form}
          name={`description-${record.id}`}
          label=""
          max={2000}
          initialValue={record.description}
        />
      ),
    }, {
      title: 'Actions',
      render: (text, record) => (
        <FlexRowContainer
          width="match_parent"
          height="wrap_content"
          justifyContent="space-around"
        >
          {record.id !== 0 &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={() => this.onUpdateClick(record)}
            >
              Update
            </styledComp.A>
          }

          {record.id !== 0 &&
            <Popconfirm
              title="Are you sure you want to delete this record?"
              placement="top"
              okText="Confirm"
              cancelText="Cancel"
              onConfirm={() => this.onDeleteClick(record)}
            >
              <styledComp.A
                width="wrap_content"
                height="wrap_content"
                fontSize="14px"
              >
                Delete
              </styledComp.A>
            </Popconfirm>
          }

          {record.id === 0 &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={this.onSaveClick}
            >
              Save
            </styledComp.A>
          }

          {record.id === 0 &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={this.onCancelClick}
            >
              Cancel
            </styledComp.A>
          }
        </FlexRowContainer>
      ),
    }];
  }

  createDataSource(efiles) {
    const dataSource = [];
    efiles.forEach((efile, index) => {
      dataSource.push({
        key: efile.getId(),
        id: efile.getId(),
        time: efile.getLong('time'),
        timeUsString: efile.getString('timeUsString'),
        type: efile.getString('type'),
        file1Id: efile.getLong('file1Id'),
        file1Url: efile.getRemoteObject('file1')?.getString('originalUrl'),
        file2Id: efile.getLong('file2Id'),
        file2Url: efile.getRemoteObject('file2')?.getString('originalUrl'),
        file3Id: efile.getLong('file3Id'),
        file3Url: efile.getRemoteObject('file3')?.getString('originalUrl'),
        description: efile.getString('description'),
      });
    });

    // If adding, add a new empty row at the top.
    if (this.state.adding) {
      dataSource.unshift({
        key: 0,
        id: 0,
        time: null,
        timeUsString: null,
        type: '',
        file1Id: null,
        file1Url: null,
        file2Id: null,
        file2Url: null,
        file3Id: null,
        file3Url: null,
        description: '',
      });
    }

    return dataSource;
  }

  async onUploadClick(efileId, fileNum) {
    try {
      this.setState({
        uploadingFile: `${efileId}_${fileNum}`,
      });

      const file = this.state.selectedFiles[`${efileId}_${fileNum}`];

      const objKeyTemplate = `efile-$agencyId$-${efileId}/files/file$pos$_${Date.now()}`;
      await aliyunOssUploader.uploadFile(
        file, this.getMyUser(), 'EFILE', efileId, fileNum - 1, objKeyTemplate, this);

      // Notify success.
      this.notifySuccess('Success', 'File has been successfully uploaded.');

      // Since the file has been uploaded, it is no longer selected.
      this[`file_input_${efileId}_${fileNum}`].value = '';
      this.setState((prevState, props) => ({
        selectedFiles: _.omit(prevState.selectedFiles, `${efileId}_${fileNum}`),
      }));

      // Wait for aliyun to callback our server.
      setTimeout(() => {
        this.getCaregiverEfiles();
      }, 2000);
    } finally {
      this.setState({
        uploadingFile: null,
      });
    }
  }

  onFileChange(e, efileId, fileNum) {
    e.stopPropagation();
    e.preventDefault();

    if (e.target.files.length > 0 && e.target.files[0]) {
      const file = e.target.files[0];
      this.setState((prevState, props) => ({
        selectedFiles: { ...prevState.selectedFiles, [`${efileId}_${fileNum}`]: file },
      }));
    }
  }

  renderFileColumn(text, record, fileNum) {
    if (record.id > 0) {
      const { selectedFiles, uploadingFile } = this.state;

      const url = record[`file${fileNum}Url`];
      const selectedFile = selectedFiles[`${record.id}_${fileNum}`];
      const isUploadingSelf = uploadingFile === `${record.id}_${fileNum}`;
      const isUploadingOther = !isUploadingSelf && !!uploadingFile;

      return (
        <FlexColContainer
          width="wrap_content"
          height="wrap_content"
          alignItems="center"
        >
          {selectedFile &&
            <styledComp.Span
              width="wrap_content"
              height="wrap_content"
              margin="0 0 6px 0"
              color="textPrimaryDark"
              fontSize="16px"
            >
              {selectedFile.name}
            </styledComp.Span>
          }

          {!selectedFile && text &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              margin="0 0 6px 0"
              href={url}
              target="_blank"
            >
              {`View file: ${text}`}
            </styledComp.A>
          }

          <FlexRowContainer
            width="wrap_content"
            height="wrap_content"
          >
            <CustomButton
              width="wrap_content"
              height="32px"
              margin="0 4px 0 0"
              backgroundColor="#32c5d2"
              fontSize="14px"
              type="primary"
              onClick={() => this[`file_input_${record.id}_${fileNum}`].click()}
            >
              SELECT...
            </CustomButton>

            <CustomButton
              width="wrap_content"
              height="32px"
              backgroundColor="#3598dc"
              fontSize="14px"
              type="primary"
              disabled={!selectedFile || isUploadingOther}
              loading={isUploadingSelf}
              onClick={() => this.onUploadClick(record.id, fileNum)}
            >
              UPLOAD
            </CustomButton>

            <input
              id={`file_input_${record.id}_${fileNum}`}
              ref={(ref) => { this[`file_input_${record.id}_${fileNum}`] = ref; }}
              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              type="file"
              style={{ display: 'none' }}
              onChange={e => this.onFileChange(e, record.id, fileNum)}
            />
          </FlexRowContainer>
        </FlexColContainer>
      );
    } else {
      return null;
    }
  }

  renderContent() {
    const { ...layoutProps } = this.props;
    const { efiles } = this.state;

    return (
      <FlexColContainer
        {...layoutProps}
        padding="20px 20px"
      >
        <CustomButton
          width="150px"
          height="34px"
          margin="0 0 20px 0"
          backgroundColor="green_haze"
          fontSize="14px"
          type="primary"
          onClick={this.onAddClick}
        >
          ADD NEW ITEM
          <Icon type="plus" />
        </CustomButton>

        <DataTables
          width="match_parent"
          height="wrap_content"
          color="green_haze"
          fontSize="12px"
          size="small"
          isInsidePanel={false}
          withColumnFilter={false}
          columns={this.createColumns()}
          dataSource={this.createDataSource(efiles)}
        />
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
CaregiverEfiles.propTypes = propTypes;

export default Form.create()(CaregiverEfiles);
